import { meanScore } from './filters';
import { METRIC_BY_STAGE } from './funnel';
import type { YougovMonthlyRow } from './yougovData.schema';

export type MetricGroupId = 'awareness' | 'perception' | 'ownership';

/**
 * The three tabs. This grouping is a frontend concern by design: the backend
 * keeps EXTENDED_METRICS as a flat list because it is fixed YouGov vocabulary,
 * not per-upload data. `index` is excluded deliberately — it reaches `monthly`
 * only because ingest does not filter by metric.
 */
/* eslint-disable i18next/no-literal-string -- YouGov wire metric keys, not copy */
export const METRIC_GROUPS: Record<MetricGroupId, string[]> = {
  awareness: ['attention', 'adaware', 'wom', 'buzz', 'impression'],
  perception: ['reputation', 'quality', 'value', 'recommend', 'satisfaction'],
  ownership: ['current_own', 'former_own'],
};

/**
 * Mirrors PERCENTAGE_METRICS in `apps/api/src/yougov/yougov.metrics.ts`. If a
 * metric is ever added to the export, both sets must change together.
 */
export const PERCENTAGE_METRICS: ReadonlySet<string> = new Set([
  'attention',
  'adaware',
  'wom',
  'current_own',
  'former_own',
]);
/* eslint-enable i18next/no-literal-string */

/** The three funnel metrics (`aided`, `consider`, `likelybuy`) are percentages too. */
const FUNNEL_METRICS: ReadonlySet<string> = new Set(Object.values(METRIC_BY_STAGE));

export function scaleFor(metric: string): 'percentage' | 'signed' {
  return PERCENTAGE_METRICS.has(metric) || FUNNEL_METRICS.has(metric) ? 'percentage' : 'signed';
}

/** Track position as a 0-100 percentage of the row's width. */
export function positionFor(value: number, metric: string): number {
  return scaleFor(metric) === 'percentage' ? value : ((value + 100) / 200) * 100;
}

export interface MetricMarker {
  brand: string;
  value: number;
  position: number;
  labelRow: number;
}

export function computeMetricRow(
  rows: YougovMonthlyRow[],
  metric: string,
  brands: string[],
): MetricMarker[] {
  return brands
    .map((brand) => {
      const value = meanScore(rows.filter((r) => r.brand === brand && r.metric === metric));
      return value === null
        ? null
        : { brand, value, position: positionFor(value, metric), labelRow: 0 };
    })
    .filter((m): m is MetricMarker => m !== null)
    .sort((a, b) => a.position - b.position);
}

/** True when every metric in the group has zero markers (e.g. a funnel-only upload). */
export function groupHasData(
  rows: YougovMonthlyRow[],
  metrics: string[],
  brands: string[],
): boolean {
  return metrics.some((metric) => computeMetricRow(rows, metric, brands).length > 0);
}

/** A marker/pill is 34px; half of that must clear the track edge on either side. */
export const MARKER_SIZE_PX = 34;

/**
 * `left` for a marker or its value pill, both absolutely positioned with
 * `-translate-x-1/2` inside a full-width track. A raw `${position}%` clips half the
 * marker off-screen at 0% (and the container's `overflow-x-auto` can't scroll left
 * to reach it) and half off the right edge at 100%. Insetting by half the marker's
 * size keeps both extremes fully inside the track without moving the plotted value:
 * at position 0 this is exactly `17px` (non-negative, no matter the track width),
 * and at position 100 it is exactly `calc(100% - 17px)`, so the marker's right edge
 * (offset + the other 17px from the translate) lands exactly on the track edge.
 */
export function markerLeft(position: number): string {
  const inset = MARKER_SIZE_PX / 2;
  return `calc(${inset}px + (100% - ${MARKER_SIZE_PX}px) * ${position} / 100)`;
}

/**
 * Real brand values cluster tightly — the reference export packs five markers into
 * ~13% of the track — so value labels collide at their true positions. Markers stay
 * exact; labels stack into as many rows as separation needs. Two fixed rows is not
 * enough: five markers that close cannot be split into two non-overlapping lanes.
 * Input must already be sorted ascending by position.
 */
export function assignLabelRows(markers: MetricMarker[], minGapPercent: number): MetricMarker[] {
  const lastPositionInRow: number[] = [];
  return markers.map((marker) => {
    let row = lastPositionInRow.findIndex((last) => marker.position - last >= minGapPercent);
    if (row === -1) row = lastPositionInRow.length;
    lastPositionInRow[row] = marker.position;
    return { ...marker, labelRow: row };
  });
}
